import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { Badge, IconButton } from "@material-ui/core";
import {
  LabelSelectItem,
  useEstimateEditorSettings,
  useEstimateEditorStore,
  useEstimateStructure,
  usePresetFieldNames,
  VisibilityButton
} from "@tools";
import { MenuButton, NavigationSwitch, PopMenu } from "@components";
import { Grid, Tooltip } from "@material-ui/core";
import { FilterListOff, PriceLabel } from "@assets";
import classnames from "classnames";

export const LabelsTurnOffManager = observer(() => {
  const { t } = useTranslation();
  const { isTemplate, isProposal, isPresetEditor } = useEstimateEditorSettings();
  const { visibilityMode } = useEstimateEditorStore();
  const structure = useEstimateStructure();
  
  const { getFieldName } = usePresetFieldNames(isProposal);
  
  const [ showFilters, setShowFilters ] = useState(false);
  
  const { usedLabels, addedLabels, displayTurnOffLabels, displayExcludeSelectedLabels, excludeTurnedOffElements } = structure;
  const { hideLabelsFilter } = structure.visibility;
  
  const anchor = useRef(null);
  
  const labels = addedLabels?.filter(({ systemId }) => usedLabels.includes(systemId)) || [];
  
  const useSeparateFilters = useMemo(() => (
    isProposal && excludeTurnedOffElements
  ), [isProposal, excludeTurnedOffElements]);
  
  const handleShowFilters = () => {
    if(isPresetEditor)
      return;
    setShowFilters(!showFilters);
  }
  
  const handleAddLabel = (label) => {
    structure.setTurnOffLabels([...(structure.displayTurnOffLabels || []), label.systemId], useSeparateFilters);
  }
  
  const handleRemoveLabel = (label) => {
    structure.setTurnOffLabels(structure.displayTurnOffLabels?.filter(l => l !== label.systemId), useSeparateFilters);
  }
  
  const handleClearFilters = () => {
    structure.resetTurnOffLabels(useSeparateFilters);
  }
  
  const handleFilterType = (e, val) => {
    structure.setExcludeSelectedLabels(val, useSeparateFilters);
  }

  return (
    <>
      <Badge
        color="primary"
        invisible={!displayTurnOffLabels?.length}
        badgeContent={displayTurnOffLabels?.length || 0}
        overlap="circular"
        max={9}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Tooltip title={getFieldName("pt-showPriceFor")}>
          <IconButton
            ref={anchor}
            color="primary"
            className={classnames(
              "text-xl",
              visibilityMode && hideLabelsFilter && "transparent-2",
              "preset-showPriceFor pt-showPriceFor"
            )}
            onClick={handleShowFilters}
          >
            {/*<TuneRounded color="primary" className="preset-showPriceForIcon" />*/}
            <PriceLabel className="preset-showPriceForIcon" />
          </IconButton>
        </Tooltip>
      </Badge>
      {
        visibilityMode &&
        <VisibilityButton
          inRow right={6}
          hidden={hideLabelsFilter}
          className={classnames(visibilityMode && hideLabelsFilter && "transparent-2")}
          onChange={structure.visibility.setHideLabelsFilter}
        />
      }
      {
        showFilters &&
        <PopMenu
          show={showFilters}
          anchor={anchor.current}
          showEmptyMessage={!usedLabels?.length}
          id="labelFilter"
          onClickAway={() => setShowFilters(false)}
          style={{ maxWidth: 240 }}
          className="py-2"
          showArrow
        >
          <Grid item container className="max-h-list w-full overflow-auto">
            <Grid item container justifyContent="space-between" alignItems="center">
              <MenuButton
                color="primary"
                icon={<FilterListOff />}
                onClick={handleClearFilters}
                disabled={ !displayTurnOffLabels?.length }
              >
                {t("views.editor.reset_configuration")}
              </MenuButton>
            </Grid>
            <Grid item container justifyContent="space-between" alignItems="center">
              <NavigationSwitch
                value={displayExcludeSelectedLabels}
                values={[
                  { value: false, label: t("common.include"), ariaLabel: "include labels" },
                  { value: true, label: t("common.exclude"), ariaLabel: "exclude labels" },
                ]}
                aria-label="comment type switch"
                onChange={handleFilterType}
                role="navigation"
                className="mb-1"
                line lighter
              />
            </Grid>
            {
              !usedLabels?.length &&
              <p className="text-secondary my-2 mx-4">
                { t(`views.editor.labels.not_found_in_${isTemplate ? "template" : "project"}`) }
              </p>
            }
            {
              labels.map((label) => (
                <LabelSelectItem
                  key={`${label.name}_${label.id}`}
                  label={label}
                  isUsed={Boolean(displayTurnOffLabels?.includes(label.systemId))}
                  onAdd={handleAddLabel}
                  onRemove={handleRemoveLabel}
                  maxWidth={178}
                >
                  {label.name}
                </LabelSelectItem>
              ))
            }
          </Grid>
        </PopMenu>
      }
    </>
  )
})